import { useContext, useEffect, useMemo, useState, useCallback, useRef } from "react";
import axios from "axios";
import { GoogleMap, Marker, InfoWindow, useLoadScript } from "@react-google-maps/api";
import { useNavigate } from "react-router-dom";
import { MyContext } from "../../Context/MyProvider";
import { buildImageUrl, applyImageFallback } from "../../utils/image";
import { showToast } from "../../showTost";
import { RiSearchLine, RiCloseLine, RiFilterLine, RiMapPinLine, RiUserLocationLine, RiRefreshLine } from "react-icons/ri";

const DEFAULT_CENTER = { lat: 23.0225, lng: 72.5714 };
const RADIUS_KM = "50";
const SEARCH_DEBOUNCE_MS = 400;
const MAX_MAP_MARKERS = 800;
const LIST_BATCH_SIZE = 50;

export default function DoctorMap() {
  const { ApiBaseUrl, ImgBaseUrl, mapKey } = useContext(MyContext);
  const navigate = useNavigate();
  const [center, setCenter] = useState(DEFAULT_CENTER);
  const [doctors, setDoctors] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [locError, setLocError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [showList, setShowList] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [visibleListCount, setVisibleListCount] = useState(LIST_BATCH_SIZE);
  const [mapBounds, setMapBounds] = useState(null);

  const { isLoaded } = useLoadScript({ googleMapsApiKey: mapKey || "" });

  const searchDebounceRef = useRef(null);
  const abortControllerRef = useRef(null);
  const listContainerRef = useRef(null);
  const mapRef = useRef(null);
  const centerRef = useRef(DEFAULT_CENTER);
  const didInitRef = useRef(false);

  centerRef.current = center;

  const markers = useMemo(
    () =>
      doctors
        .map((d) => {
          const lat = Number(d.latitude);
          const lng = Number(d.longitude);
          if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
          return { ...d, lat, lng };
        })
        .filter(Boolean),
    [doctors]
  );

  // Markers inside the current map viewport (so 5000+ doctors stay fast).
  // Falls back to the first batch when bounds are not known yet.
  const visibleMarkers = useMemo(() => {
    if (!mapBounds) return markers.slice(0, MAX_MAP_MARKERS);
    const inBounds = markers.filter((d) =>
      d.lat <= mapBounds.north &&
      d.lat >= mapBounds.south &&
      d.lng <= mapBounds.east &&
      d.lng >= mapBounds.west
    );
    return inBounds.slice(0, MAX_MAP_MARKERS);
  }, [markers, mapBounds]);

  const renderedList = useMemo(
    () => markers.slice(0, visibleListCount),
    [markers, visibleListCount]
  );

  const loadDoctors = useCallback(async (lat, lng, query = "") => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();

    setLoading(true);
    try {
      const res = await axios.post(
        `${ApiBaseUrl}/customer/map_doctor_list`,
        {
          latitude: String(lat),
          longitude: String(lng),
          radius: RADIUS_KM,
          search_text: query,
        },
        { signal: abortControllerRef.current.signal }
      );
      setDoctors(res.data.doctor_list || []);
      setVisibleListCount(LIST_BATCH_SIZE);
      setSelected(null);
      if (!(res.data.doctor_list || []).length && !query) {
        showToast({ title: "No doctors found nearby", id: "info" });
      }
    } catch (e) {
      if (e?.name !== "AbortError" && e?.code !== "ERR_CANCELED") {
        showToast({ title: "Could not load doctors", id: "error" });
      }
    } finally {
      setLoading(false);
    }
  }, [ApiBaseUrl]);

  // Initial load: user location or default area (runs once).
  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;
    if (!navigator.geolocation) {
      setLocError("Geolocation not supported");
      loadDoctors(DEFAULT_CENTER.lat, DEFAULT_CENTER.lng);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const next = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setCenter(next);
        loadDoctors(next.lat, next.lng);
      },
      () => {
        setLocError("Location permission denied — showing default area");
        loadDoctors(DEFAULT_CENTER.lat, DEFAULT_CENTER.lng);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Debounced text search against the current center (skips the first run).
  const searchSkipFirst = useRef(true);
  useEffect(() => {
    if (searchSkipFirst.current) {
      searchSkipFirst.current = false;
      return;
    }
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      const c = centerRef.current;
      loadDoctors(c.lat, c.lng, searchQuery.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(searchDebounceRef.current);
  }, [searchQuery, loadDoctors]);

  useEffect(() => () => {
    abortControllerRef.current?.abort();
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
  }, []);

  const handleListScroll = () => {
    const container = listContainerRef.current;
    if (!container || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = container;
    if (scrollTop + clientHeight >= scrollHeight - 200) {
      setVisibleListCount((prev) => Math.min(prev + LIST_BATCH_SIZE, markers.length));
    }
  };

  const openDoctor = (doc) => {
    const title = doc.title || doc.name || "Doctor Profile";
    sessionStorage.setItem(
      "Docprofile",
      JSON.stringify({
        id: doc.id,
        department_id: doc.department_id || "0",
        title,
        path: "/map",
        pagename: "Map",
      })
    );
    navigate(`/doctor-profile/${doc.id}`);
  };

  const geocodeLocation = (address) => new Promise((resolve, reject) => {
    if (!window.google?.maps?.Geocoder) {
      reject(new Error("Geocoder not ready"));
      return;
    }
    new window.google.maps.Geocoder().geocode({ address }, (results, status) => {
      if (status === "OK" && results?.[0]) resolve(results[0].geometry.location);
      else reject(new Error(status));
    });
  });

  const handleSearchLocation = async (e) => {
    e.preventDefault();
    const q = searchQuery.trim();
    if (!q) return;
    // Always refresh the doctor search first (works even without geocoding).
    const c = centerRef.current;
    loadDoctors(c.lat, c.lng, q);
    // Then try to move the map to a matching place name.
    try {
      const loc = await geocodeLocation(q);
      setCenter({ lat: loc.lat(), lng: loc.lng() });
    } catch {
      // Not a place name — doctor-text search results still apply.
    }
  };

  const recenterToUser = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const next = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setCenter(next);
        loadDoctors(next.lat, next.lng, searchQuery.trim());
        setLocError("");
      },
      () => setLocError("Unable to get your location")
    );
  };

  const handleMapLoad = (map) => {
    mapRef.current = map;
  };

  const handleMapIdle = () => {
    const map = mapRef.current;
    if (!map) return;
    const b = map.getBounds();
    if (!b) return;
    const ne = b.getNorthEast();
    const sw = b.getSouthWest();
    setMapBounds({
      north: ne.lat(),
      east: ne.lng(),
      south: sw.lat(),
      west: sw.lng(),
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* In-flow sticky toolbar: sits BELOW the global pill header (z-999),
          so it never overlaps it. Sticks at ~110px (header height + gaps). */}
      <div className="sticky top-[104px] z-30 bg-white/95 backdrop-blur border-b border-gray-200 shadow-sm px-4 py-3 sm:px-6">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-lg sm:text-xl font-bold text-rm-ink">Doctors Near You</h1>
            <p className="text-gray-600 text-xs sm:text-sm">
              {loading ? "Searching…" : `${markers.length} doctor${markers.length === 1 ? "" : "s"} found`}
              {searchQuery ? ` for "${searchQuery}"` : ` within ${RADIUS_KM} km`}
            </p>
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <form onSubmit={handleSearchLocation} className="relative w-full sm:w-[320px] flex-1">
              <RiSearchLine className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search doctor, specialty, or location…"
                className="w-full pl-10 pr-10 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0d9488] focus:border-transparent"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  aria-label="Clear search"
                >
                  <RiCloseLine size={18} />
                </button>
              )}
            </form>
            <button
              className="hidden sm:flex items-center gap-2 px-4 py-2 bg-[#0d9488] text-white rounded-lg text-sm font-medium hover:bg-[#0b7d73] transition whitespace-nowrap"
              onClick={recenterToUser}
            >
              <RiUserLocationLine size={18} /> My Location
            </button>
            <button
              className="sm:hidden p-2 bg-gray-100 rounded-lg"
              onClick={() => setShowList(!showList)}
              aria-label={showList ? "Hide list" : "Show doctor list"}
            >
              <RiFilterLine size={22} />
            </button>
            <button
              className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition disabled:opacity-60"
              onClick={() => {
                const c = centerRef.current;
                loadDoctors(c.lat, c.lng, searchQuery.trim());
              }}
              title="Refresh results"
              disabled={loading}
              aria-label="Refresh results"
            >
              <RiRefreshLine size={20} className={loading ? "animate-spin text-[#0d9488]" : "text-gray-600"} />
            </button>
          </div>
        </div>
      </div>

      {isMobile && showList && (
        <div
          className="fixed inset-0 z-[1000] bg-black/30 lg:hidden"
          onClick={() => setShowList(false)}
          aria-hidden="true"
        />
      )}

      <main className="max-w-7xl mx-auto px-4 py-4 pb-8 sm:px-6">
        <div className="grid lg:grid-cols-12 gap-4">
          <aside
            className={`
              lg:col-span-4 col-span-12
              ${isMobile
                ? (showList
                  ? "fixed top-0 bottom-0 right-0 z-[1001] w-[360px] max-w-[92vw] transform transition-transform duration-200 translate-x-0"
                  : "fixed top-0 bottom-0 right-0 z-[1001] w-[360px] max-w-[92vw] transform transition-transform duration-200 translate-x-full")
                : ""}
              bg-white rounded-xl border shadow-sm overflow-hidden flex flex-col
            `}
          >
            <div className="p-3 border-b bg-white flex items-center justify-between">
              <h2 className="font-semibold text-rm-ink">Doctors ({markers.length})</h2>
              {isMobile && (
                <button onClick={() => setShowList(false)} className="p-1 text-gray-500 hover:text-rm-ink" aria-label="Close list">
                  <RiCloseLine size={22} />
                </button>
              )}
            </div>
            <div
              ref={listContainerRef}
              onScroll={handleListScroll}
              className="flex-1 overflow-auto p-2 space-y-2"
              style={{ maxHeight: isMobile ? "calc(100dvh - 60px)" : "calc(100vh - 280px)", minHeight: "320px" }}
            >
              {loading ? (
                <div className="flex items-center justify-center h-48 text-gray-500">Loading doctors…</div>
              ) : markers.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-48 text-gray-500 text-center px-4">
                  <RiMapPinLine size={32} className="mb-2 opacity-50" />
                  <p>{searchQuery ? "No doctors match your search" : "No doctors in this area"}</p>
                </div>
              ) : (
                <>
                  {renderedList.map((d) => (
                    <button
                      key={d.id}
                      type="button"
                      className={`w-full text-left flex gap-3 p-3 rounded-lg border transition ${
                        selected?.id === d.id
                          ? "bg-[#0d9488]/10 border-[#0d9488]"
                          : "border-gray-100 hover:bg-gray-50"
                      }`}
                      onClick={() => {
                        setSelected(d);
                        setCenter({ lat: d.lat, lng: d.lng });
                        if (isMobile) setShowList(false);
                      }}
                      onDoubleClick={() => openDoctor(d)}
                    >
                      <img
                        src={buildImageUrl(ImgBaseUrl, d.logo, "doctor")}
                        onError={(e) => applyImageFallback(e, ImgBaseUrl, "doctor")}
                        alt=""
                        className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                        loading="lazy"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="font-medium truncate text-sm">{d.title || d.name}</p>
                        <p className="text-xs text-gray-500 truncate">{d.subtitle || d.zone_name}</p>
                        <p className="text-xs text-gray-400 truncate">{d.address}</p>
                      </div>
                      {selected?.id === d.id && (
                        <span className="text-[#0d9488] self-center">
                          <RiMapPinLine size={18} fill="#0d9488" />
                        </span>
                      )}
                    </button>
                  ))}
                  {visibleListCount < markers.length && (
                    <div className="p-2 text-center text-xs text-gray-500">
                      Showing {renderedList.length} of {markers.length} — scroll for more
                    </div>
                  )}
                </>
              )}
            </div>
          </aside>

          <section className="lg:col-span-8 col-span-12">
            <div className="h-[62dvh] min-h-[420px] lg:h-[calc(100vh-280px)] lg:min-h-[520px] rounded-xl overflow-hidden border bg-gray-100 relative">
              {!mapKey || !isLoaded ? (
                <div className="h-full flex flex-col items-center justify-center text-gray-500 p-4 text-center">
                  <RiMapPinLine size={32} className="mb-2 opacity-50" />
                  <p>{mapKey ? "Loading map…" : "Google Map key not configured"}</p>
                </div>
              ) : (
                <>
                  <GoogleMap
                    mapContainerStyle={{ width: "100%", height: "100%" }}
                    center={center}
                    zoom={12}
                    options={{
                      streetViewControl: false,
                      mapTypeControl: false,
                      fullscreenControl: false,
                      zoomControl: true,
                      gestureHandling: "cooperative",
                    }}
                    onLoad={handleMapLoad}
                    onIdle={handleMapIdle}
                    onClick={() => setSelected(null)}
                  >
                    <Marker
                      position={center}
                      label={{ text: "You", color: "white", fontSize: "12px", fontWeight: "bold" }}
                      options={{
                        icon: {
                          path: "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z",
                          fillColor: "#0d9488",
                          fillOpacity: 1,
                          strokeWeight: 2,
                          strokeColor: "white",
                          scale: 1.2,
                          anchor: { x: 12, y: 24 },
                        },
                        zIndex: 1000,
                      }}
                    />
                    {visibleMarkers.map((d) => (
                      <Marker
                        key={d.id}
                        position={{ lat: d.lat, lng: d.lng }}
                        onClick={() => {
                          setSelected(d);
                          if (isMobile) setShowList(false);
                        }}
                        options={{
                          icon: {
                            path: "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z",
                            fillColor: selected?.id === d.id ? "#dc2626" : "#0d9488",
                            fillOpacity: 1,
                            strokeWeight: 2,
                            strokeColor: "white",
                            scale: 1.1,
                            anchor: { x: 12, y: 24 },
                          },
                          zIndex: selected?.id === d.id ? 100 : 10,
                        }}
                      />
                    ))}
                    {selected && (
                      <InfoWindow
                        position={{ lat: selected.lat, lng: selected.lng }}
                        onCloseClick={() => setSelected(null)}
                      >
                        <div className="max-w-[240px] p-1">
                          <p className="font-semibold text-sm text-rm-ink">{selected.title || selected.name}</p>
                          <p className="text-xs text-gray-600">{selected.subtitle || selected.zone_name}</p>
                          <p className="text-xs text-gray-500">{selected.address}</p>
                          <button
                            className="mt-2 w-full text-[#0d9488] text-sm font-medium hover:underline"
                            onClick={() => openDoctor(selected)}
                          >
                            View profile
                          </button>
                        </div>
                      </InfoWindow>
                    )}
                  </GoogleMap>

                  <div className="absolute bottom-3 right-3 flex flex-col gap-2">
                    <button
                      className="p-2 bg-white rounded-lg shadow-lg hover:bg-gray-50 transition"
                      onClick={recenterToUser}
                      title="My location"
                      aria-label="Center map on my location"
                    >
                      <RiUserLocationLine size={20} className="text-[#0d9488]" />
                    </button>
                    <button
                      className="p-2 bg-white rounded-lg shadow-lg hover:bg-gray-50 transition lg:hidden"
                      onClick={() => setShowList(!showList)}
                      title={showList ? "Hide list" : "Show doctor list"}
                      aria-label={showList ? "Hide list" : "Show doctor list"}
                    >
                      <RiFilterLine size={20} className="text-[#0d9488]" />
                    </button>
                  </div>

                  {markers.length > MAX_MAP_MARKERS && (
                    <div className="absolute bottom-3 left-3 max-w-[70%] bg-white rounded-lg shadow-lg px-3 py-2 text-xs text-gray-600">
                      Showing {visibleMarkers.length} of {markers.length} doctors in view. Zoom or pan for more.
                    </div>
                  )}
                </>
              )}

              {locError && (
                <div className="absolute top-3 left-3 right-3 sm:right-auto sm:max-w-[320px] bg-amber-50 border border-amber-200 text-amber-800 text-xs px-3 py-2 rounded-lg shadow-lg flex items-center gap-2">
                  <RiMapPinLine size={16} className="flex-shrink-0" />
                  <span>{locError}</span>
                </div>
              )}
            </div>
          </section>
        </div>
      </main>

      <style>{`
        @keyframes dm-slide-up {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}